import { Lock } from "lucide-react";
import { useEntitlements } from "../../hooks/useEntitlements";
import { UpgradePrompt } from "./UpgradePrompt";
import { cn } from "../../lib/utils";

/**
 * LockedFeature — gates `children` behind the current user's entitlements.
 *
 * - Entitled: renders children unchanged.
 * - Loading: renders children (optimistic — avoids a paywall flash before the
 *   subscription doc resolves).
 * - Not entitled: renders children behind a non-interactive, dimmed overlay with
 *   a lock icon, followed by an <UpgradePrompt/> deep-linking to /pricing.
 *
 * Gating is resolved via useEntitlements().canUseFeature(feature), so the single
 * source of truth (tier-config) drives both UI and server enforcement.
 *
 * @param {{ feature: string, tier?: "lite"|"pro", message?: string,
 *           className?: string, children: import("react").ReactNode }} props
 */
export function LockedFeature({ feature, tier, message, className, children }) {
  const { canUseFeature, loading } = useEntitlements();
  const entitled = loading || canUseFeature(feature);

  if (entitled) {
    return children;
  }

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <div className="relative">
        <div
          aria-hidden="true"
          className="pointer-events-none select-none opacity-50 blur-[1px]"
        >
          {children}
        </div>
        <div
          data-testid="locked-feature-overlay"
          className="absolute inset-0 flex items-center justify-center rounded-2xl bg-white/30 dark:bg-slate-950/30"
        >
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-900/80 px-3 py-1 text-xs font-semibold text-white shadow-sm">
            <Lock className="h-3.5 w-3.5" aria-hidden="true" />
            Locked
          </span>
        </div>
      </div>
      <UpgradePrompt feature={feature} tier={tier} message={message} />
    </div>
  );
}

export default LockedFeature;
